import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import CustomAccordion from '@v2/components/ui/CustomAccordion/CustomAccordion';

import { ICartBoxLine, ICartBox } from '@interfaces';
import { useAddCartModal, useSwitchToCustomModal } from '@components/common/presentational/modal/common';
import Clickable from '@v2/components/common/presentational/clickables/default';

import { setActiveBox, setActiveChildBox, useAddActiveBoxToCart } from '@app/store/rtk.cart.store';
import { useIsSwapping } from '@v2/components/common/products/utils';
import Product from '@components/common/builder/product';
import stylesPersonalized from '@pages/shop/partial/personalized.styles';
import Star from '@v2/components/common/recommendations/star';
import Picker from '@pages/shop/partial/picker';
import { trackCartBox, packAndTrack } from '@v2/services/analytics.service';

import { imgV2 as img } from '@styles/utils';
import BoxStageFooter from '@v2/components/common/boxes/stage.footer';
import arrowUp from '@img/checkout/arrow-up.svg';
import arrowDown from '@img/checkout/arrow-down.svg';
import { ContentLoader } from '@v2/components/common/presentational/loader/loader';
import { useHistoryWithReferrer } from '@v2/utils/history';
import { useUpdateActiveChild } from '../../quiz/utils';
import { useCartAllBoxes } from '@app/store/cart.selectors';
import cloneDeep from 'lodash.clonedeep';

function Box({ box, index, active, setActive }: { box: ICartBox, index: number, active: number, setActive: Function }) {
    const dispatch = useDispatch();
    const history = useHistoryWithReferrer();

    const addActiveBoxToCart = useAddActiveBoxToCart();
    const updateActiveChild = useUpdateActiveChild();

    const [showCartModal] = useAddCartModal(index);
    const isSwapping = useIsSwapping();
    const [adding, setAdding] = useState(false);

    const addToCart = () => {
        setAdding(true);
        trackCartBox(box);
        dispatch(setActiveBox({ index }));
        addActiveBoxToCart();

        if (!isSwapping) {
            showCartModal();
        }

        setAdding(false);
    }
    const setToActive = () => {
        dispatch(setActiveChildBox({ index }));
        dispatch(setActiveBox({ index }));
    }

    const editBox = () => {
        setToActive();
        const newBox = cloneDeep(box);
        newBox.survey.completed = true;
        newBox.child.recommendedPacksEnabled = false;
        newBox.title = 'Custom Bundle';
        newBox.type = 'custom';

        updateActiveChild(null, newBox);
        history.pushWithReferrer('/box-builder');

        closeSwitchToCustomModal();
        packAndTrack('Switched To Custom Bundle',
            [{ 'view': 'Survey', 'section': 'Personalized', ...newBox }]
        );
    }

    const seeWhy = () => {
        setToActive();
        const newBox = cloneDeep(box);
        newBox.survey.completed = false;
        newBox.survey.previousScreen = 'none';
        newBox.survey.activeScreen = 'overview';

        updateActiveChild(null, newBox);
        history.pushWithReferrer('/survey');

        packAndTrack('See Why Clicked',
            [{ 'view': 'Shop', 'section': 'Personalized' }]);
    }

    const chooseSize = () => {
        setToActive();
        const newBox = cloneDeep(box);
        newBox.survey.completed = false;
        newBox.survey.previousScreen = 'none';
        newBox.survey.activeScreen = 'size';

        updateActiveChild(null, newBox);
        history.pushWithReferrer('/survey');

        packAndTrack('Choose Size Clicked',
            [{ 'view': 'Shop', 'section': 'Personalized' }]);
    }

    const [showSwitchToCustomModal, closeSwitchToCustomModal] = useSwitchToCustomModal(
        editBox,
        (box && box.child && box.child.childName)
            ? box.child.childName
            : null
    );

    return (
        <CustomAccordion key={index} activeKey={`${active}`} className="personalized-body">
            <div className="icon-star">
                <Star name={box.child.name} index={index} />
            </div>
            <CustomAccordion.Toggle
                onClick={() => {
                    setActive(active === index ? null : index);
                    packAndTrack('Expand Box Clicked', [{ 'view': 'Shop', 'section': 'Personalized', ...box }]);
                }}
                eventKey={`${index}`}
                className={'toggle'}>
                <div className="personalized-subheader">
                    <div className="personalized-info">
                        <span className="personalized-info-name">{box.child.name}'s Bundle</span>
                        <img className="icon-dropdown" src={active === index ? arrowUp : arrowDown} alt="Drowpdown Icon" />
                        <Clickable className="personalized-product-total-count desktop" onClick={() => chooseSize()}>
                            {box.count} items |&nbsp;
                        </Clickable>
                        {box.subscribe
                                ?
                                <div className="personalized-prices">
                                    <Clickable className="personalized-product-total-count mobile" onClick={() => chooseSize()}>
                                        {box.count} items |&nbsp;
                                    </Clickable>
                                    <span className="subtotal">
                                        ${box.priceDefault
                                            ? box.priceDefault.toFixed(2)
                                            : (box.price / (100 - box.blueprint.discount) * 100).toFixed(2)}
                                    </span>
                                    <span className="total">
                                        ${box.price.toFixed(2)}
                                    </span>
                                </div>
                                :
                                <div className="personalized-prices">
                                    <Clickable className="personalized-product-total-count mobile" onClick={() => chooseSize()}>
                                        {box.count} items |&nbsp;
                                    </Clickable>
                                    <span className="no-discount-total">
                                        ${box.price.toFixed(2)}
                                    </span>
                                </div>
                            }
                    </div>
                    {(box.lines && Object.keys(box.lines).length > 0)
                        ?
                        <div className="personalized-action">
                            <button
                                tabIndex={0}
                                disabled={!box.incomplete || adding}
                                className={`button primary desktop ${box.incomplete ? 'red' : 'green'}`}
                                onClick={() => box.incomplete ? addToCart() : {}}>
                                <span>
                                    {isSwapping
                                        ? (box.incomplete ? 'Select' : 'SELECTED!')
                                        : (box.incomplete ? 'Add to bag' : 'ADDED!')
                                    }
                                </span>
                            </button>
                        </div>
                        : <></>
                    }
                </div>
            </CustomAccordion.Toggle>

            <CustomAccordion.Collapse eventKey={`${index}`} className={'content'}>
                <>
                    <div className="personalized-products-wrapper">
                        {Object.values(box.lines).map((line: ICartBoxLine) =>
                            <div key={line.product.id} className="personalized-products">
                                <Product
                                    key={line.product.id}
                                    product={line.product}
                                    disabled={true}
                                    setAddedCallback={() => { }}
                                    pdpNavigation={'modal'} />
                                <span className="product-count">x {line.count}</span>
                            </div>
                        )}
                    </div>
                    <button
                        tabIndex={0}
                        disabled={!box.incomplete}
                        className={`button primary mobile ${box.incomplete ? 'red' : 'green'}`}
                        onClick={() => box.incomplete ? addToCart() : {}}>
                        <span>
                            {isSwapping
                                ? (box.incomplete ? 'Select' : 'SELECTED!')
                                : (box.incomplete ? 'Add to bag' : 'ADDED!')
                            }
                        </span>
                    </button>
                    <div className="personalized-products-info">
                        <p>A balanced combination of food based on their age and developmental milestones</p>
                        {box.incomplete &&
                            <div className="personalized-info-actions">
                                <Clickable onClick={() => {
                                    showSwitchToCustomModal();
                                    packAndTrack('Edit Personalized Bundle Clicked',
                                        [{ 'view': 'Shop', 'section': 'Personalized', ...box }]
                                    );
                                }}>
                                    <img src={img('shop/personalized/Edit.svg')} alt={'Edit Icon'} />
                                    <span>Edit</span>
                                </Clickable>
                                <Clickable onClick={() => seeWhy()}>
                                    <img src={img('shop/personalized/Question.svg')} alt={'Question Icon'} />
                                    <span>See why</span>
                                </Clickable>
                            </div>
                        }
                    </div>
                    <BoxStageFooter box={box} />
                </>
            </CustomAccordion.Collapse>
        </CustomAccordion>
    )
}

export default function Personalized({ category, loading }: { category: any, loading: boolean }) {
    const history = useHistoryWithReferrer();
    const boxes = useCartAllBoxes();
    const [active, setActive] = useState(null);
    const showPersonalized = Object.values(boxes).filter((box: ICartBox) => {
        return box.child && box.lines && box.stage && box.blueprint && box.child.recommendedPacksEnabled !== false;
    }).length > 0 && !history.location.pathname.includes('/swap');

    useEffect(() => {
        if (active === null) {
            Object.values(boxes).forEach((box: ICartBox, index: number) => {
                if (box.child && box.lines && box.stage && box.blueprint && box.child.recommendedPacksEnabled !== false) {
                    setActive(index);
                }
            });
        }
    }, [boxes]);

    return (
        <div id={category.id}>
            {showPersonalized
                ?
                <div className="category personalized">
                    <div className={stylesPersonalized.personalizedContainer}>
                        <div className="personalized-header">
                            {category.title}
                        </div>
                        {boxes && Object.values(boxes).map((box: ICartBox, index: number) =>
                            box.child && box.lines && box.stage && box.blueprint && box.child.recommendedPacksEnabled !== false &&
                            <Box
                                key={index}
                                box={box}
                                index={index}
                                active={active}
                                setActive={setActive}
                            />
                        )}
                    </div>
                </div>
                :
                <></>
            }
            <Picker category={category} hideHeading={showPersonalized} />

            <ContentLoader loading={loading} />
        </div>
    );
}
